// Vitrine.js — a moldura de TODA seção que mostra livros.
//
// ================= O PROBLEMA QUE ELA RESOLVE =================
//
// Cada vitrine do site abria com a mesma linha: um ícone branco e um título em
// negrito. Só isso. "Produtos em Destaque", "Você poderia gostar", "Publicados
// recentemente", "Da mesma área" — quatro seções que dizem coisas MUITO
// diferentes, desenhadas exatamente iguais, empilhadas no mesmo fundo escuro
// sem nada entre elas. O dono resumiu em 16/09/2026: "estão muito simples e
// feias".
//
// Ele estava descrevendo três defeitos de uma vez:
//
//  1. NENHUMA HIERARQUIA. Um h2 de 20px sozinho não separa uma seção da
//     anterior; ele só anuncia que o assunto mudou, sem dizer o quanto.
//  2. NENHUM CONTEXTO. O título dizia O QUE era a lista e nunca POR QUE ela
//     existe. "Produtos em Destaque" não conta que aqueles cinco livros foram
//     escolhidos a dedo e estão mais baratos por isso.
//  3. NENHUMA BORDA. Sem uma linha fechando o cabeçalho, as capas de uma
//     vitrine encostavam no título da vitrine seguinte, e a home virava uma
//     coluna contínua de cards sem começo nem fim.
//
// ================= O DESENHO =================
//
//    ── CHAPÉU (laranja, pequeno, com um traço curto à esquerda)
//    [◈] Título grande                          [ Ver todos → ]
//        uma frase explicando de onde vem a lista
//    ────────────────────────────────────────────────────────────  (2px)
//
// O chapéu é o que dá o contexto que faltava, em três palavras. O quadrado do
// ícone e o botão "Ver todos" usam a MESMA receita de todo o site — contorno de
// 2px na cor da sombra e a sombra sólida deslocada —, que é o que faz o
// cabeçalho pertencer à mesma página que os cards embaixo dele. A linha de 2px
// fecha o cabeçalho e é ela que dá o "começo de seção" que não existia.
//
// O "Ver todos" aparece DUAS VEZES de propósito: botão pequeno no canto do
// cabeçalho no computador, e barra larga embaixo dos livros no celular. Ali em
// cima, num toque, ele seria um alvo de 14px perdido ao lado do título; e no
// celular o fim da lista é onde a pessoa de fato termina de olhar.
import React from "react";
import { Link } from "react-router-dom";
import Reveal from "../../ui/Reveal";
import { TituloDeSecao } from "../../ui/TituloDeSecao";

const subirAoTopo = () => window.scrollTo({ top: 0, behavior: "smooth" });

/**
 * @param {string}  chapeu     Duas ou três palavras dizendo de onde vem a lista.
 * @param {string}  titulo     O nome da seção.
 * @param {string}  icone      Classe do Bootstrap Icons, sem o "bi " na frente.
 * @param {string}  descricao  Uma frase. Verdadeira — ver o comentário abaixo.
 * @param {{to: string, texto?: string}} verTodos  Destino do "Ver todos".
 */
const Vitrine = ({
  chapeu,
  titulo,
  // `icone` ficou sem uso em 18/10/2026 (o título agora segue o desenho dos
  // outros sites, sem o quadrado do ícone); continua aceito para as chamadas
  // antigas não quebrarem.
  icone, // eslint-disable-line no-unused-vars
  destaque,
  descricao,
  verTodos,
  children,
  // O respiro da SEÇÃO, separado do `className` de propósito. Ele precisa ser
  // SUBSTITUÍVEL, e não sobrescrito: o bootstrap.min.css declara `.px-4` e
  // `.px-0` com `!important`, e entre dois `!important` quem ganha é o que vem
  // depois na folha — `.px-4`, sempre. Passar `padding=""` (como faz a página
  // do livro, que já tem o respiro dela) é a única forma de zerar isto sem
  // escrever um terceiro `!important`.
  padding = "px-4 md:px-8 py-10 md:py-12",
  className = "",
  ...rest
}) => (
  <section className={padding + " " + className} {...rest}>
    <div className="max-w-[1280px] mx-auto">
      <Reveal className="mb-[2rem] md:mb-[2.5rem]">
        {/* O CABEÇALHO NO DESENHO DOS OUTROS DOIS SITES (18/10/2026): chapéu,
            título na League Gothic com a última palavra em laranja, o
            losango e a frase. Ver ui/TituloDeSecao. O "Ver todos" fica à
            direita, alinhado embaixo, como antes. */}
        <TituloDeSecao
          chapeu={chapeu}
          titulo={titulo}
          destaque={destaque}
          subtitulo={descricao}
          acao={
            verTodos && (
              <Link
                to={verTodos.to}
                onClick={subirAoTopo}
                className="group/vt hidden sm:inline-flex shrink-0 items-center gap-2 rounded-controle
                           border-2 border-insigne-sombra bg-insigne-navy-soft px-4 py-2
                           text-sm font-bold text-white shadow-[3px_3px_0_0_rgb(var(--c-sombra))]
                           transition-[transform,box-shadow,color] duration-200
                           hover:-translate-x-[2px] hover:-translate-y-[2px] hover:text-insigne-orange
                           hover:shadow-[5px_5px_0_0_rgb(var(--c-sombra))]"
              >
                {verTodos.texto || "Ver todos"}
                <i className="bi bi-arrow-right transition-transform duration-200 group-hover/vt:translate-x-1" />
              </Link>
            )
          }
        />
      </Reveal>

      {children}

      {verTodos && (
        <Link
          to={verTodos.to}
          onClick={subirAoTopo}
          className="sm:hidden mt-6 flex items-center justify-center gap-2 rounded-controle
                     border-2 border-insigne-sombra bg-insigne-navy-soft py-3
                     text-sm font-bold text-white shadow-[3px_3px_0_0_rgb(var(--c-sombra))]"
        >
          {verTodos.texto || "Ver todos"}
          <i className="bi bi-arrow-right" />
        </Link>
      )}
    </div>
  </section>
);

export default Vitrine;
